import { Injectable } from '@jack-henry/frontend-utils/di';
import {
    AccountReconClient,
    AchClient,
    AchCompaniesClient,
    AchCompanyModelDto,
    ApplicationNameType2Dto,
    CompaniesClient,
    CompanyClient,
    InformationReportingProductFeatureModelDto,
    PositivePayCompanyConfigurationClient,
    PositivePayInstitutionConfigurationClient,
    ProductFeatureConfigurationsClient,
    ProductFeatureModelDto,
    RemoteDepositProductModelDto,
    SaveCompanyStopPaymentRequestDto,
    SavePositivePayCompanyConfigurationRequestModelDto,
    SecCodeModelDto,
    StopPaymentClient,
    WausauStatementsProductModelDto,
    WiresClient,
    WireTransferProductFeatureModelDto,
} from '@treasury/api/bo';
import { AchCompany } from '@treasury/domain/company/entities/ach-company.entity';
import { SecCode } from '@treasury/domain/company/product-features/entities/sec-code.entity';
import { CompanyOptionModel } from '../../../arp/company-option-model.entity';
import { AccountProductConfiguration } from '../account/entities/account-product-configuration.entity';
import { WireCompany } from '../wires/entities/wire-company.entity';
import * as ProductFeatures from './entities';
import { DepositAccount } from './entities/deposit-account.entity';
import { InstitutionOptionModel } from './entities/product-features/institution-option-model.entity';
import { ProductFeatureAccount } from './entities/product-features/product-feature-account.entity';
import { mapProductFeatureDto } from './mappers/map-product-feature';
import type { FeatureMappingType } from './types';
import { FeatureApiType } from './types/product-feature.enum';

@Injectable()
export class CompanyProductFeatureService {
    constructor(
        private readonly companiesClient: CompaniesClient,
        private readonly companyClient: CompanyClient,
        private readonly positivePayCompanyConfigurationClient: PositivePayCompanyConfigurationClient,
        private readonly positivePayInstitutionConfigurationClient: PositivePayInstitutionConfigurationClient,
        private readonly stopPaymentClient: StopPaymentClient,
        private readonly accountReconClient: AccountReconClient,
        private readonly achClient: AchClient,
        private readonly wiresClient: WiresClient,
        private readonly productFeatureConfigurationsClient: ProductFeatureConfigurationsClient,
        private readonly achCompaniesClient: AchCompaniesClient
    ) {}

    public async changeProductStatus(companyId: number, productId: number, status: string) {
        const companyIdString = companyId.toString();
        const productIdString = productId.toString();
        return (
            await this.companiesClient.companiesProductStatus(companyIdString, productIdString, {
                status,
            })
        ).data;
    }

    public async updateProductFeature<T extends keyof FeatureMappingType>(
        companyId: number,
        featureId: number,
        productFeature:
            | ProductFeatures.WireProductFeature
            | ProductFeatures.AchProductFeature
            | ProductFeatures.PositivePayProductFeature
            | ProductFeatures.StopPaymentProductFeature
            | ProductFeatures.ReportingProductFeature
            | ProductFeatures.TransferProductFeature
            | ProductFeatures.ElectronicDocumentsProductFeature
            | ProductFeatures.WausauEStatementsProductFeature
            | ProductFeatures.BillPayProductFeature
            | ProductFeatures.RemoteDepositCaptureProductFeature
            | ProductFeatures.AccountReconProductFeature,
        featureType: T
    ): Promise<FeatureMappingType[T]> {
        if (featureType === FeatureApiType.StopPayment) {
            const stopPaymentFeature = productFeature as ProductFeatures.StopPaymentProductFeature;
            const dto: SaveCompanyStopPaymentRequestDto = {
                approvalCount: stopPaymentFeature.approvalCount,
                accountIds: stopPaymentFeature.accountIds,
                companyId,
                applicationNameType: ApplicationNameType2Dto.BackOffice,
            };
            const response = await this.stopPaymentClient.stopPaymentSaveCompanyConfiguration(dto);
            if (response.ok) {
                return productFeature as FeatureMappingType[T];
            } else {
                throw new Error('Failed to save stop payment configuration');
            }
        }
        if (featureType === FeatureApiType.Reporting) {
            await this.saveEnableIntuitFormattedExports(
                companyId,
                (productFeature as ProductFeatures.ReportingProductFeature)
                    .enableIntuitFormattedExports
            );
        }

        if (featureType === FeatureApiType.RemoteDeposit) {
            return (await this.editRemoteDepositCaptureProductFeature(
                companyId,
                featureId,
                productFeature as ProductFeatures.RemoteDepositCaptureProductFeature
            )) as FeatureMappingType[T];
        }

        /**
         * The Wausau eStatements api put call is different from the other features,
         * the call does not return data on success,
         * so we need to handle it by checking for OK then returning the passed in object
         */
        if (featureType === FeatureApiType.Wausau) {
            const result = await this.companiesClient.companiesProductFeaturesPut(
                companyId,
                featureId,
                productFeature.toDto()
            );
            if (result.ok) {
                return productFeature as FeatureMappingType[T];
            }
        }

        /**
         * The Account Recon api put call is different from the other features,
         * the call does not return data on success,
         * so we need to handle it by checking for OK then returning the passed in object
         */
        if (featureType === FeatureApiType.AccountRecon) {
            const featureDto = (
                productFeature as unknown as ProductFeatures.AccountReconProductFeature
            ).toDto();
            const result =
                await this.accountReconClient.accountReconSaveCompanyProductConfiguration(
                    featureDto
                );
            if (result.ok) {
                return productFeature as FeatureMappingType[T];
            }
        }

        const result = await this.companiesClient.companiesProductFeaturesPut(
            companyId,
            featureId,
            productFeature.toDto()
        );

        return mapProductFeatureDto(result.data, featureType) as FeatureMappingType[T];
    }

    public async saveProductFeature<T extends keyof FeatureMappingType>(
        companyId: number,
        productFeature:
            | ProductFeatures.WireProductFeature
            | ProductFeatures.AchProductFeature
            | ProductFeatures.StopPaymentProductFeature
            | ProductFeatures.ReportingProductFeature
            | ProductFeatures.TransferProductFeature
            | ProductFeatures.ElectronicDocumentsProductFeature
            | ProductFeatures.ForeignCurrencyWireProductFeature
            | ProductFeatures.AgileticsWebEscrowProductFeature
            | ProductFeatures.WausauEStatementsProductFeature
            | ProductFeatures.BillPayProductFeature
            | ProductFeatures.RemoteDepositCaptureProductFeature,
        featureType: T
    ): Promise<FeatureMappingType[T]> {
        if (featureType === FeatureApiType.StopPayment) {
            const stopPaymentFeature = productFeature as ProductFeatures.StopPaymentProductFeature;
            const dto: SaveCompanyStopPaymentRequestDto = {
                approvalCount: stopPaymentFeature.approvalCount,
                accountIds: stopPaymentFeature.accountIds,
                companyId,
                applicationNameType: ApplicationNameType2Dto.BackOffice,
            };
            const response = await this.stopPaymentClient.stopPaymentSaveCompanyConfiguration(dto);
            if (response.ok) {
                return productFeature as FeatureMappingType[T];
            } else {
                throw new Error('Failed to save stop payment configuration');
            }
        }
        if (featureType === FeatureApiType.Reporting) {
            await this.saveEnableIntuitFormattedExports(
                companyId,
                (productFeature as ProductFeatures.ReportingProductFeature)
                    .enableIntuitFormattedExports
            );
        }

        if (featureType === FeatureApiType.RemoteDeposit) {
            return (await this.addRemoteDepositCaptureProductFeature(
                companyId,
                productFeature as ProductFeatures.RemoteDepositCaptureProductFeature
            )) as FeatureMappingType[T];
        }

        const result = await this.companiesClient.companiesProductFeaturesPost(
            `${companyId}`,
            productFeature.toDto()
        );

        return mapProductFeatureDto(result.data, featureType) as FeatureMappingType[T];
    }

    public async getCompanyProductFeature<T extends keyof FeatureMappingType>(
        companyId: number,
        featureId: number,
        featureType: T
    ) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let response: any;
        let feature: ProductFeatureModelDto | undefined = undefined;

        if (featureType === FeatureApiType.AccountRecon) {
            return this.getAccountReconProductFeature(companyId, featureId);
        }

        try {
            response = await this.companiesClient.companiesProductFeaturesGet(companyId, featureId);
            feature = response.data;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            if (e.errorCode === '' && featureType === FeatureApiType.Wausau) {
                /**
                 * The Wausau eStatements api get call is different from the other features,
                 * because one of the data points for Wausau is `errorMessage` TmHttpClient treats this as an error
                 * so we need to handle it by catching the error and getting the dto from the error object.
                 */
                feature = e.dto as WausauStatementsProductModelDto;
            }
        }

        if (feature === undefined) {
            throw new Error(`Feature not found: ${featureType}`);
        }

        if (featureType === FeatureApiType.Reporting) {
            return this.getReportingProductFeature(
                companyId,
                feature as InformationReportingProductFeatureModelDto
            );
        }

        return mapProductFeatureDto(feature, featureType) as FeatureMappingType[T];
    }

    public async getNewWireProductFeature(): Promise<ProductFeatures.WireProductFeature> {
        return new ProductFeatures.WireProductFeature(
            (
                await this.productFeatureConfigurationsClient.productFeatureConfigurationsGetByName(
                    'WireTransfer'
                )
            ).data as unknown as WireTransferProductFeatureModelDto
        );
    }

    public async getCompanyProductFeatures(companyId: number) {
        const companyIdString = companyId.toString();
        const features = (await this.companiesClient.companiesProductFeaturesAll(companyIdString))
            .data;
        return features.map(feature => new ProductFeatures.CompanyProductFeature(feature));
    }

    public async getCompanyAvailableProductFeatures(companyId: number) {
        const features = (
            await this.companiesClient.companiesGetAvailableProductsForCompany(companyId)
        ).data;
        return features.map(feature => new ProductFeatures.FiProductFeature(feature));
    }

    public async getDepositAccounts(companyId: number) {
        return (await this.companiesClient.companiesDepositAccounts(companyId)).data.map(
            account => new DepositAccount(account)
        );
    }

    public async getWiresAccounts(
        productFeatureId: number,
        companyId: number
    ): Promise<WireCompany> {
        return new WireCompany(
            (await this.wiresClient.wiresAccounts(productFeatureId, companyId, false)).data
        );
    }

    public async getAchCompanies(companyId: number, companyName: string): Promise<AchCompany[]> {
        const response = await this.achCompaniesClient.achCompaniesSearchAll(companyId, {
            companyName: companyName,
        });
        return response.data.map(achCompany => new AchCompany(achCompany));
    }

    public async addAchCompany(companyId: number, achCompanies: AchCompany[]) {
        return await this.achClient.achCompaniesAllPost(
            companyId,
            achCompanies.map(a => a.toDto() as AchCompanyModelDto)
        );
    }

    public async updateAchCompanyStatus(featureId: number, company: AchCompany) {
        const response = await this.achClient.achUpdateStatus(
            featureId,
            company.id,
            company.toDto() as AchCompanyModelDto
        );
        return new AchCompany(response.data);
    }

    public async getAchSecCodes(featureId: number) {
        const response = await this.achClient.achSecCodes(featureId);
        return response.data.map(code => new SecCode(code));
    }

    public async updateAchSecCodes(featureId: number, achCompanyId: number, secCodes: SecCode[]) {
        const response = await this.achClient.achCompaniesAllPut(
            featureId,
            achCompanyId,
            secCodes.map(code => code.toDto() as SecCodeModelDto)
        );
        return response.data.map(code => new SecCode(code));
    }

    public async removeAchCompanies(featureId: number, companies: AchCompany[]) {
        // There isn't an endpoint for removing and array of ACH companies so we have to loop through
        // and remove them one at a time
        try {
            const promises = companies.map(company =>
                this.achClient.achCompaniesDelete(featureId, company.id)
            );
            await Promise.all(promises);
            return true;
        } catch (error) {
            throw new Error(`Failed to remove ACH companies: ${error}`);
        }
    }

    public async getPositivePayInstitutionSettings() {
        const response =
            await this.positivePayInstitutionConfigurationClient.positivePayInstitutionConfigurationGetAllOptions();
        return response.data.map(option => new InstitutionOptionModel(option));
    }

    public async getPositivePayCompanyConfiguration(companyId: number) {
        const companyGuid = (
            await this.companyClient.companyGetCompanyGuidUniqueId(companyId, {
                maxAgeInSeconds: 300,
            })
        ).data;
        const response =
            await this.positivePayCompanyConfigurationClient.positivePayCompanyConfigurationGetAllOptions(
                companyGuid
            );
        return response.data.map(option => new CompanyOptionModel(option));
    }

    public async savePositivePayCompanyConfiguration(
        companyId: number,
        options: CompanyOptionModel[],
        accounts: ProductFeatureAccount[]
    ) {
        const companyGuid = (
            await this.companyClient.companyGetCompanyGuidUniqueId(companyId, {
                maxAgeInSeconds: 300,
            })
        ).data;
        const request: SavePositivePayCompanyConfigurationRequestModelDto = {
            productFeatureAccounts: accounts.map(account => account.toDto()),
            options: options.map(option => option.toDto()),
        };
        const response =
            await this.positivePayCompanyConfigurationClient.positivePayCompanyConfigurationSaveProductConfiguration(
                companyGuid,
                request
            );
        return response.data;
    }

    public async deleteToAchCompany(companyId: number, achCompanyId: number) {
        return await this.achClient.achCompaniesDelete(companyId, achCompanyId);
    }

    private async getAccountReconProductFeature(
        companyId: number,
        featureId: number
    ): Promise<ProductFeatures.AccountReconProductFeature> {
        const response =
            await this.accountReconClient.accountReconGetCompanyProductConfiguration(companyId);
        const { accounts } = response.data;
        const feature = new ProductFeatures.AccountReconProductFeature();
        // Account recon doesn't actually use the feature id for anything, but it's required for the save logic
        feature.id = featureId;
        feature.companyId = response.data.companyId;
        if (accounts) {
            feature.accounts = accounts.map(account => new AccountProductConfiguration(account));
        }
        return feature;
    }

    private async getReportingProductFeature(
        companyId: number,
        feature: InformationReportingProductFeatureModelDto
    ): Promise<ProductFeatures.ReportingProductFeature> {
        const response = await this.companiesClient.companiesGetQfxEnabledFlag(companyId);
        feature.enableIntuitFormattedExports = response.data.enabledFlag;
        return new ProductFeatures.ReportingProductFeature(feature);
    }

    private async addRemoteDepositCaptureProductFeature(
        companyId: number,
        feature: ProductFeatures.RemoteDepositCaptureProductFeature
    ): Promise<ProductFeatures.RemoteDepositCaptureProductFeature> {
        const [remoteDepositProductTypeId] = (
            await this.companiesClient.companiesGetAvailableRemoteDepositProductTypes()
        ).data;

        const product = {
            remoteDepositProductTypeId,
            ssoCustomerId: feature.customerId,
            type: 'RemoteDeposits',
        };
        const result = (
            await this.companiesClient.companiesAddRemoteDepositProduct(
                companyId,
                product as RemoteDepositProductModelDto
            )
        ).data;

        return mapProductFeatureDto(
            result,
            FeatureApiType.RemoteDeposit
        ) as ProductFeatures.RemoteDepositCaptureProductFeature;
    }

    private async editRemoteDepositCaptureProductFeature(
        companyId: number,
        productId: number,
        feature: ProductFeatures.RemoteDepositCaptureProductFeature
    ): Promise<ProductFeatures.RemoteDepositCaptureProductFeature> {
        const [remoteDepositProductTypeId] = (
            await this.companiesClient.companiesGetAvailableRemoteDepositProductTypes()
        ).data;

        const product = {
            id: productId,
            remoteDepositProductTypeId,
            ssoCustomerId: feature.customerId,
            type: 'RemoteDeposits',
        };

        const result = (
            await this.companiesClient.companiesEditRemoteDepositProduct(
                companyId,
                product as RemoteDepositProductModelDto
            )
        ).data;

        return mapProductFeatureDto(
            result,
            FeatureApiType.RemoteDeposit
        ) as ProductFeatures.RemoteDepositCaptureProductFeature;
    }

    private async saveEnableIntuitFormattedExports(
        companyId: number,
        enableIntuitFormattedExports: boolean
    ) {
        await this.companiesClient.companiesSaveQfxCompanyFlag(companyId, {
            newFlag: enableIntuitFormattedExports ? 'Y' : 'N',
        });
    }
}
